import type { Look, OriginId, Pose } from '../shared/types.ts';
import { BASES } from '../shared/items.ts';
import { ACCENTS, CLOTHS, SKINS } from '../shared/content.ts';
import { SURVIVORS, survivorOf } from '../shared/survivors.ts';
import { drawChar, drawWeaponArt, hasChar } from './art.ts';

const INK = '#1a1612';

export function facingOf(aim: number): { dir: 'up' | 'down' | 'side'; flip: boolean } {
  const c = Math.cos(aim);
  const s = Math.sin(aim);
  if (Math.abs(c) >= Math.abs(s)) return { dir: 'side', flip: c < 0 };
  return { dir: s < 0 ? 'up' : 'down', flip: false };
}

function box(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, fill: string, ink = INK): void {
  const px = Math.round(x);
  const py = Math.round(y);
  ctx.fillStyle = ink;
  ctx.fillRect(px - 1, py - 1, w + 2, h + 2);
  ctx.fillStyle = fill;
  ctx.fillRect(px, py, w, h);
}

function blot(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, fill: string): void {
  ctx.fillStyle = fill;
  ctx.fillRect(Math.round(x), Math.round(y), w, h);
}

// While a walker is mid-hop, drawActor lifts and tilts the whole sprite. The
// shadow stays pinned to the ground (and shrinks a touch) so it reads as a hop.
export const gaitGround: { m: DOMMatrix | null; lift: number } = { m: null, lift: 0 };

function shadow(ctx: CanvasRenderingContext2D, x: number, y: number, w: number): void {
  ctx.fillStyle = 'rgba(12,10,8,0.35)';
  if (!gaitGround.m) {
    ctx.fillRect(Math.round(x - w / 2), Math.round(y - 2), w, 3);
    return;
  }
  const sw = w - Math.round(gaitGround.lift);
  ctx.save();
  ctx.setTransform(gaitGround.m);
  ctx.fillRect(Math.round(x - sw / 2), Math.round(y + gaitGround.lift - 2), sw, 3);
  ctx.restore();
}

function line(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, color: string, thick: number): void {
  const steps = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0)));
  ctx.fillStyle = color;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    ctx.fillRect(Math.round(x0 + (x1 - x0) * t), Math.round(y0 + (y1 - y0) * t), thick, thick);
  }
}

export function drawWeapon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  aim: number,
  weapon: string | null,
  origin: OriginId | undefined,
  swing: boolean,
): void {
  const handX = x + 3;
  const handY = y - 11;
  const base = weapon ? BASES[weapon] : null;
  if (!base || base.slot !== 'weapon') {
    if (origin === 'knuckle') blot(ctx, handX - 1, handY - 1, 3, 3, '#e7d3c4');
    return;
  }
  if (drawWeaponArt(ctx, weapon!, handX, handY, aim, swing, base.kind === 'melee')) return;
  const art = base.art;
  const tint = base.tint;
  const heavy = art === 'maul' || art === 'hammer' || art === 'axe' || art === 'spike';
  const long = art === 'blade' || art === 'machete' || art === 'keel' || art === 'saw' || art === 'rifle';
  const reach = (swing ? 14 : 10) * (long ? 1.2 : heavy ? 1.1 : 1);
  const x1 = handX + Math.cos(aim) * reach;
  const y1 = handY + Math.sin(aim) * reach;
  if (base.kind === 'melee') {
    line(ctx, handX, handY, x1, y1, heavy ? '#6d5432' : tint, 2);
    if (heavy) blot(ctx, x1 - 3, y1 - 3, 6, 5, tint);
    else if (art === 'wrench') blot(ctx, x1 - 2, y1 - 3, 4, 3, '#d7d2c6');
    else if (art === 'tooth' || art === 'dipper') blot(ctx, x1 - 1, y1 - 2, 3, 4, '#f4f1ea');
    else blot(ctx, x1 - 1, y1 - 1, 3, 3, tint === '#d9d3c8' ? '#a34532' : '#f4f1ea');
    return;
  }
  if (art === 'can') {
    line(ctx, handX, handY, x1, y1, '#5c564e', 2);
    blot(ctx, x1 - 2, y1 - 3, 5, 5, tint);
    return;
  }
  line(ctx, handX, handY, x1, y1, art === 'needle' || art === 'prod' ? '#5c564e' : tint, art === 'needle' ? 1 : 2);
  if (art === 'scatter' || art === 'box') {
    blot(ctx, x1 - 2, y1 - 2, 4, 4, tint);
    blot(ctx, x1 - 1, y1 - 1, 2, 2, '#1a1612');
  } else if (art === 'prong') {
    blot(ctx, x1 - 3, y1 - 1, 2, 2, '#e7e0d2');
    blot(ctx, x1 + 1, y1 - 1, 2, 2, '#e7e0d2');
  } else blot(ctx, x1 - 1, y1 - 1, 3, 3, art === 'needle' ? '#e0b15a' : art === 'prod' ? '#7fbfb2' : '#2a241c');
}

/** Reskin sheet for a person: Brine and Peck are fixed, everyone else is the survivor they picked. */
export function spriteFor(role: 'player' | 'brine' | 'peck', look: Look | undefined): string | null {
  if (role === 'brine') return 'ws_caravan_trader';
  if (role === 'peck') return 'ws_rust_mechanic';
  return look ? SURVIVORS[survivorOf(look)].slug : null;
}

export function drawPerson(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  opts: {
    look?: Look;
    role?: 'player' | 'brine' | 'peck';
    pose: Pose;
    aim: number;
    time: number;
    weapon?: string | null;
    origin?: OriginId;
    flash?: boolean;
    dead?: boolean;
    /** Animation key (actor id); lets the sprite know when a pose started. */
    id?: number | string;
  },
): void {
  const role = opts.role ?? 'player';
  const slug = spriteFor(role, opts.look);
  if (hasChar(slug)) {
    shadow(ctx, x, y, 12);
    drawChar(ctx, slug!, opts.id ?? 'preview', x, y, opts.aim, opts.pose, Boolean(opts.dead), Boolean(opts.flash));
    if (!opts.dead && role === 'player') drawWeapon(ctx, x, y, opts.aim, opts.weapon ?? null, opts.origin, opts.pose === 'swing');
    return;
  }
  const look = opts.look ?? { skin: 4, cloth: 3, accent: 2, head: 'tuft' as const };
  const skin = role === 'brine' ? '#d9d3c2' : role === 'peck' ? '#c4845a' : SKINS[look.skin] ?? SKINS[0];
  const cloth = role === 'brine' ? '#2c403c' : role === 'peck' ? '#6d5432' : CLOTHS[look.cloth] ?? CLOTHS[0];
  const accent = role === 'brine' ? '#7fbfb2' : role === 'peck' ? '#e0b15a' : ACCENTS[look.accent] ?? ACCENTS[0];
  const ink = opts.flash ? '#f4f1ea' : INK;
  const { dir, flip } = facingOf(opts.aim);
  const frame = opts.pose === 'walk' ? Math.floor(opts.time * 8) % 2 : 0;
  const step = frame ? 1 : 0;

  ctx.save();
  if (opts.dead) ctx.globalAlpha = 0.45;
  shadow(ctx, x, y, role === 'peck' ? 12 : 10);
  if (flip && dir === 'side') {
    ctx.translate(Math.round(x), 0);
    ctx.scale(-1, 1);
    ctx.translate(-Math.round(x), 0);
  }

  const tall = role === 'brine';
  const short = role === 'peck';
  const bodyH = tall ? 12 : short ? 6 : 8;
  const bodyW = short ? 10 : 8;
  const bodyY = y - (short ? 12 : 14) - (tall ? 2 : 0);
  const headW = short ? 10 : 10;
  const headH = short ? 8 : 9;
  const headY = bodyY - headH + 1;

  if (!tall) {
    box(ctx, x - 4, y - 7 - step, 3, 6, '#2a241c', ink);
    box(ctx, x + 1, y - 7 - (1 - step), 3, 6, '#2a241c', ink);
    blot(ctx, x - 4, y - 2 - step, 3, 2, '#1a1612');
    blot(ctx, x + 1, y - 2 - (1 - step), 3, 2, '#3a3228');
  }

  box(ctx, x - bodyW / 2, bodyY, bodyW, bodyH, cloth, ink);
  blot(ctx, x - bodyW / 2, y - 8, bodyW, 2, accent);

  if (role === 'peck') {
    blot(ctx, x - 8, bodyY + 2, 3, 3, '#8a8478');
    blot(ctx, x + 5, bodyY + 2, 3, 3, '#8a8478');
  }

  box(ctx, x - headW / 2, headY, headW, headH, skin, ink);

  if (dir !== 'up') {
    const eyeY = headY + 3;
    if (dir === 'side') {
      blot(ctx, x + 1, eyeY, 2, 2, '#f4f1ea');
      blot(ctx, x + 2, eyeY, 1, 1, INK);
    } else {
      blot(ctx, x - 3, eyeY, 2, 2, '#f4f1ea');
      blot(ctx, x + 2, eyeY, 2, 2, '#f4f1ea');
      blot(ctx, x - 2, eyeY, 1, 1, INK);
      blot(ctx, x + 3, eyeY, 1, 1, INK);
    }
    if (role === 'brine') {
      blot(ctx, x - 1, headY + 1, 2, 2, '#f4f1ea');
      blot(ctx, x, headY + 1, 1, 1, INK);
    }
    if (role === 'peck') {
      blot(ctx, x - 4, headY + 3, 3, 2, '#7fbfb2');
      blot(ctx, x + 1, headY + 3, 3, 2, '#7fbfb2');
    }
  }

  if (role === 'player') drawHead(ctx, x, headY, look.head, accent, cloth, ink);
  if (role === 'brine') blot(ctx, x - 3, y - 6, 6, 2, '#7fbfb2');

  ctx.restore();

  if (!opts.dead && role === 'player') {
    drawWeapon(ctx, x, y, opts.aim, opts.weapon ?? null, opts.origin, opts.pose === 'swing');
  }
}

function drawHead(
  ctx: CanvasRenderingContext2D,
  x: number,
  headY: number,
  head: Look['head'],
  accent: string,
  cloth: string,
  ink: string,
): void {
  if (head === 'tuft') blot(ctx, x - 2, headY - 3, 4, 3, '#2a241c');
  if (head === 'mohawk') {
    blot(ctx, x - 1, headY - 5, 2, 5, accent);
    blot(ctx, x - 1, headY - 5, 2, 1, ink);
  }
  if (head === 'antenna') {
    blot(ctx, x - 4, headY - 5, 1, 4, ink);
    blot(ctx, x + 3, headY - 6, 1, 5, ink);
    blot(ctx, x - 5, headY - 6, 2, 2, accent);
    blot(ctx, x + 3, headY - 7, 2, 2, accent);
  }
  if (head === 'hood') {
    blot(ctx, x - 6, headY - 1, 12, 3, cloth);
    blot(ctx, x - 6, headY + 2, 2, 6, cloth);
    blot(ctx, x + 4, headY + 2, 2, 6, cloth);
    blot(ctx, x - 6, headY - 1, 12, 1, ink);
  }
  if (head === 'bald') blot(ctx, x - 1, headY + 1, 2, 1, accent);
}

export function drawRoach(ctx: CanvasRenderingContext2D, x: number, y: number, fast: boolean, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const shell = fast ? '#a34532' : '#5e7a45';
  const frame = pose === 'walk' ? Math.floor(time * 10) % 2 : 0;
  shadow(ctx, x, y, 12);
  const leg = frame ? 1 : 0;
  blot(ctx, x - 7, y - 4 - leg, 2, 3, '#2a241c');
  blot(ctx, x - 3, y - 3 + leg, 2, 3, '#2a241c');
  blot(ctx, x + 2, y - 4 - leg, 2, 3, '#2a241c');
  blot(ctx, x + 5, y - 3 + leg, 2, 3, '#2a241c');
  box(ctx, x - 6, y - 10, 12, 7, shell, ink);
  blot(ctx, x - 4, y - 8, 3, 2, fast ? '#6d3028' : '#3e4a32');
  blot(ctx, x + 1, y - 7, 3, 2, fast ? '#6d3028' : '#3e4a32');
  if (fast) blot(ctx, x - 1, y - 9, 2, 2, '#e0b15a');
  blot(ctx, x - 5, y - 12, 1, 3, ink);
  blot(ctx, x + 4, y - 12, 1, 3, ink);
}

export function drawToad(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const puff = pose === 'windup' ? 2 : 0;
  const frame = pose === 'walk' ? Math.floor(time * 6) % 2 : 0;
  shadow(ctx, x, y, 16);
  box(ctx, x - 8 - puff, y - 11, 16 + puff * 2, 9, '#8a9a55', ink);
  blot(ctx, x - 4, y - 6, 8, 3, '#c4b46a');
  box(ctx, x - 2, y - 16, 4, 6, '#8a8478', ink);
  blot(ctx, x - 1, y - 14, 2, 2, '#a34532');
  blot(ctx, x - 6, y - 14, 3, 3, '#d9d3c2');
  blot(ctx, x + 3, y - 14, 3, 3, '#d9d3c2');
  blot(ctx, x - 5, y - 13, 1, 1, INK);
  blot(ctx, x + 4, y - 13, 1, 1, INK);
  if (pose === 'windup') blot(ctx, x - 7 - puff, y - 8, 2, 2, '#a34532');
  blot(ctx, x - 5, y - 3 - frame, 3, 2, '#3e4a32');
  blot(ctx, x + 2, y - 3 - (1 - frame), 3, 2, '#3e4a32');
}

export function drawNewt(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const frame = pose === 'walk' ? Math.floor(time * 12) % 2 : 0;
  shadow(ctx, x, y, 10);
  blot(ctx, x - 8, y - 4, 4, 2, '#2f5a40');
  box(ctx, x - 5, y - 7, 10, 5, '#3f6b52', ink);
  blot(ctx, x - 2, y - 5, 5, 2, '#7fbfa3');
  blot(ctx, x + 4, y - 8, 2, 2, '#e0b15a');
  blot(ctx, x + 5, y - 8, 1, 1, INK);
  blot(ctx, x - 4, y - 3 - frame, 2, 2, '#1a1612');
  blot(ctx, x + 1, y - 3 - (1 - frame), 2, 2, '#1a1612');
}

export function drawSlug(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const puff = pose === 'windup' ? 1 : 0;
  shadow(ctx, x, y, 16);
  box(ctx, x - 8, y - 8, 16, 7, '#c4b07a', ink);
  blot(ctx, x - 4, y - 6, 3, 2, '#8a7048');
  blot(ctx, x + 2, y - 5, 4, 2, '#8a7048');
  blot(ctx, x - 3, y - 12, 1, 5, ink);
  blot(ctx, x + 2, y - 13, 1, 6, ink);
  blot(ctx, x - 4, y - 13, 2, 2, '#1a1612');
  blot(ctx, x + 2, y - 14, 2, 2, '#1a1612');
  if (pose === 'windup') blot(ctx, x - 1, y - 4, 3 + puff, 2, '#a34532');
  const slide = Math.sin(time * 3) > 0 ? 1 : 0;
  blot(ctx, x - 6, y - 2 + slide, 12, 2, '#8a9a55');
}

export function drawPanback(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const frame = pose === 'walk' ? Math.floor(time * 6) % 2 : 0;
  shadow(ctx, x, y, 18);
  box(ctx, x - 9, y - 8, 18, 6, '#e7e0d2', ink);
  blot(ctx, x - 6, y - 7, 5, 2, '#b7b1a4');
  blot(ctx, x + 2, y - 6, 4, 2, '#8a8478');
  blot(ctx, x - 1, y - 5, 2, 1, INK);
  blot(ctx, x - 7, y - 3 - frame, 3, 2, '#7a7468');
  blot(ctx, x + 4, y - 3 - (1 - frame), 3, 2, '#7a7468');
  if (pose === 'windup') blot(ctx, x + 8, y - 6, 3, 2, '#f4f1ea');
}

export function drawSkip(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const hop = pose === 'walk' && Math.floor(time * 10) % 2 === 0 ? -3 : 0;
  shadow(ctx, x, y, 8);
  box(ctx, x - 4, y - 7 + hop, 8, 4, '#f4f1ea', ink);
  blot(ctx, x + 2, y - 8 + hop, 2, 2, INK);
  blot(ctx, x - 3, y - 3, 2, 2, '#b7b1a4');
  blot(ctx, x + 1, y - 3, 2, 2, '#b7b1a4');
}

export function drawFilament(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const lean = pose === 'walk' ? 1 : 0;
  shadow(ctx, x, y, 6);
  line(ctx, x, y - 2, x + lean, y - 16, '#e7e0d2', 2);
  blot(ctx, x - 2 + lean, y - 18, 4, 3, Math.sin(time * 6) > 0 ? '#e7e0d2' : '#7fbfa3');
  blot(ctx, x + lean, y - 17, 1, 1, ink);
  blot(ctx, x - 3, y - 2, 2, 2, '#5c564e');
  blot(ctx, x + 1, y - 2, 2, 2, '#5c564e');
}

export function drawSlick(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const slide = pose === 'walk' ? Math.floor(time * 8) % 2 : 0;
  shadow(ctx, x, y, 16);
  box(ctx, x - 8 + slide, y - 5, 16, 4, '#1a1814', ink);
  blot(ctx, x - 3, y - 4, 6, 1, '#3f6b5a');
  blot(ctx, x + 2, y - 6, 2, 2, '#7fbfa3');
}

export function drawDrifter(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const step = pose === 'walk' ? Math.floor(time * 6) % 2 : 0;
  shadow(ctx, x, y, 10);
  box(ctx, x - 4, y - 14, 8, 10, '#6a5340', ink);
  blot(ctx, x - 5, y - 12, 2, 6, '#3a3228');
  blot(ctx, x + 3, y - 12, 2, 6, '#3a3228');
  blot(ctx, x - 3, y - 4 - step, 2, 3, '#1a1612');
  blot(ctx, x + 1, y - 4 - (1 - step), 2, 3, '#1a1612');
  blot(ctx, x - 1, y - 10, 2, 2, '#c4a574');
}

export function drawSleeper(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const open = pose === 'walk' || Math.sin(time * 1.4) > 0.4;
  shadow(ctx, x, y, 14);
  box(ctx, x - 7, y - 6, 14, 5, '#3a4034', ink);
  blot(ctx, x - 3, y - 5, 6, 2, '#2a3024');
  if (open) {
    blot(ctx, x + 3, y - 7, 3, 3, '#e0b15a');
    blot(ctx, x + 4, y - 6, 1, 1, ink);
  }
}

export function drawMite(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const hop = pose === 'walk' && Math.floor(time * 12) % 2 === 0 ? -2 : 0;
  shadow(ctx, x, y, 8);
  blot(ctx, x - 5, y - 3, 2, 2, ink);
  blot(ctx, x + 3, y - 3, 2, 2, ink);
  box(ctx, x - 3, y - 7 + hop, 6, 4, '#d7e4ea', ink);
  blot(ctx, x - 1, y - 6 + hop, 2, 2, Math.sin(time * 10) > 0 ? '#7fbfb2' : '#e7e0d2');
}

export function drawNail(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const lean = pose === 'walk' ? 1 : 0;
  shadow(ctx, x, y, 6);
  line(ctx, x, y - 2, x + lean, y - 16, '#8a5a32', 2);
  blot(ctx, x - 2 + lean, y - 18, 4, 3, pose === 'windup' || Math.sin(time * 7) > 0 ? '#e07a3d' : '#e0b15a');
  blot(ctx, x + lean, y - 17, 1, 1, ink);
  blot(ctx, x - 2, y - 2, 4, 2, '#5c564e');
}

export function drawMast(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const puff = pose === 'windup' ? 2 : 0;
  const frame = pose === 'walk' ? Math.floor(time * 4) % 2 : 0;
  shadow(ctx, x, y, 18);
  blot(ctx, x - 2, y - 16, 3, 10, '#5c564e');
  box(ctx, x - 8 - puff, y - 18, 16 + puff * 2, 5, '#7fbfb2', ink);
  blot(ctx, x - 4, y - 17, 8, 2, '#e7e0d2');
  blot(ctx, x - 1, y - 16, 2, 2, pose === 'windup' ? '#e07a3d' : '#1a1612');
  blot(ctx, x - 6, y - 3 - frame, 3, 2, '#5c564e');
  blot(ctx, x + 3, y - 3 - (1 - frame), 3, 2, '#5c564e');
}

export function drawTick(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const step = pose === 'walk' ? Math.floor(time * 10) % 2 : 0;
  shadow(ctx, x, y, 8);
  box(ctx, x - 3, y - 7, 6, 4, '#e7d3c4', ink);
  blot(ctx, x - 4, y - 4 - step, 2, 2, '#8a8078');
  blot(ctx, x + 2, y - 4 - (1 - step), 2, 2, '#8a8078');
  if (Math.sin(time * 8) < 0.85) blot(ctx, x + 1, y - 6, 1, 1, ink);
}

export function drawCrab(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const frame = pose === 'walk' ? Math.floor(time * 5) % 2 : 0;
  shadow(ctx, x, y, 16);
  box(ctx, x - 7, y - 8, 14, 6, '#8a6a42', ink);
  blot(ctx, x - 4, y - 7, 8, 2, '#c4a574');
  blot(ctx, x - 9, y - 9, 3, 3, '#e7d3c4');
  blot(ctx, x + 6, y - 9, 3, 3, '#e7d3c4');
  blot(ctx, x - 5, y - 3 - frame, 3, 2, '#5c564e');
  blot(ctx, x + 2, y - 3 - (1 - frame), 3, 2, '#5c564e');
}

export function drawDrawer(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const puff = pose === 'windup' ? 1 : 0;
  shadow(ctx, x, y, 20);
  box(ctx, x - 9, y - 14, 18, 12, '#6a5340', ink);
  blot(ctx, x - 6, y - 10, 12, 2, '#1a1612');
  blot(ctx, x - 6, y - 6, 12, 2, '#1a1612');
  const glow = 0.45 + Math.sin(time * 4) * 0.25;
  ctx.fillStyle = `rgba(224,122,61,${glow.toFixed(2)})`;
  ctx.fillRect(Math.round(x - 2), Math.round(y - 12 - puff), 4, 2);
  blot(ctx, x - 6, y - 3, 3, 2, '#3a3228');
  blot(ctx, x + 3, y - 3, 3, 2, '#3a3228');
}

export function drawLeaker(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const slide = pose === 'walk' ? Math.floor(time * 8) % 2 : 0;
  shadow(ctx, x, y, 16);
  box(ctx, x - 8 + slide, y - 8, 16, 6, '#3d6b22', ink);
  blot(ctx, x - 4, y - 7, 8, 2, '#8fbf6a');
  blot(ctx, x + 3, y - 10, 3, 3, '#d7e46a');
  blot(ctx, x - 6, y - 3, 3, 2, '#1a1814');
  blot(ctx, x + 3, y - 3, 3, 2, '#1a1814');
}

export function drawCount(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const puff = pose === 'windup' ? 2 : 0;
  shadow(ctx, x, y, 20);
  box(ctx, x - 9 - puff, y - 16, 18 + puff * 2, 12, '#2f4a28', ink);
  blot(ctx, x - 5, y - 12, 10, 2, '#8fbf6a');
  const tick = Math.floor(time * 3) % 3;
  ctx.fillStyle = '#d7e46a';
  ctx.fillRect(Math.round(x - 4 + tick * 3), Math.round(y - 18), 2, 3);
  blot(ctx, x - 6, y - 4, 3, 2, '#1a1814');
  blot(ctx, x + 3, y - 4, 3, 2, '#1a1814');
}

export function drawDog(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number): void {
  const frame = pose === 'walk' ? Math.floor(time * 8) % 2 : 0;
  const tail = Math.sin(time * 8) > 0 ? -2 : 1;
  shadow(ctx, x, y, 12);
  blot(ctx, x + 5, y - 8 + tail, 4, 2, '#8a5a3c');
  box(ctx, x - 6, y - 8, 12, 5, '#c4a15a');
  box(ctx, x + 4, y - 12, 5, 5, '#d7b56a');
  blot(ctx, x + 7, y - 10, 1, 1, INK);
  blot(ctx, x + 3, y - 13, 2, 2, '#8a5a3c');
  blot(ctx, x - 1, y - 7, 3, 2, '#e0b15a');
  blot(ctx, x - 5, y - 4 - frame, 2, 3, '#8a5a3c');
  blot(ctx, x - 1, y - 4 - (1 - frame), 2, 3, '#8a5a3c');
  blot(ctx, x + 3, y - 4 - frame, 2, 3, '#8a5a3c');
}

export function drawHound(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const frame = pose === 'walk' ? Math.floor(time * 12) % 2 : 0;
  shadow(ctx, x, y, 14);
  blot(ctx, x - 9, y - 9, 3, 2, '#3a3228');
  box(ctx, x - 7, y - 9, 13, 5, '#5c4a3a', ink);
  box(ctx, x + 4, y - 13, 6, 5, '#6d5432', ink);
  blot(ctx, x + 8, y - 12, 1, 1, '#e07058');
  blot(ctx, x + 5, y - 15, 2, 2, '#3a3228');
  blot(ctx, x - 3, y - 8, 5, 1, '#8a6a42');
  blot(ctx, x - 6, y - 4 - frame, 2, 3, '#3a3228');
  blot(ctx, x - 2, y - 4 - (1 - frame), 2, 3, '#3a3228');
  blot(ctx, x + 2, y - 4 - frame, 2, 3, '#3a3228');
  blot(ctx, x + 5, y - 4 - (1 - frame), 2, 3, '#3a3228');
}

export function drawWalker(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const frame = pose === 'walk' ? Math.floor(time * 6) % 2 : 0;
  const puff = pose === 'windup' ? 1 : 0;
  shadow(ctx, x, y, 12);
  blot(ctx, x - 4, y - 8 - frame, 2, 8, '#5c564e');
  blot(ctx, x + 2, y - 8 - (1 - frame), 2, 8, '#5c564e');
  box(ctx, x - 6, y - 20, 12, 12, '#8a8478', ink);
  blot(ctx, x - 4, y - 18, 8, 2, '#6a6560');
  blot(ctx, x - 3, y - 15, 6, 3, pose === 'windup' ? '#e07a3d' : '#3a3228');
  box(ctx, x - 3, y - 25 - puff, 6, 5, '#6a6560', ink);
  blot(ctx, x - 1, y - 24 - puff, 2, 2, '#e0b15a');
}

export function drawMoth(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const flap = Math.floor(time * 16) % 2;
  const lift = 8 + Math.sin(time * 4) * 2;
  shadow(ctx, x, y, 8);
  const wy = y - lift - 6;
  ctx.fillStyle = flap ? 'rgba(170,220,200,0.75)' : 'rgba(210,240,230,0.85)';
  ctx.fillRect(Math.round(x - 9), Math.round(wy - (flap ? 3 : 1)), 7, flap ? 7 : 5);
  ctx.fillRect(Math.round(x + 2), Math.round(wy - (flap ? 3 : 1)), 7, flap ? 7 : 5);
  box(ctx, x - 2, wy - 2, 4, 8, '#e7f0e8', ink);
  blot(ctx, x - 1, wy - 1, 2, 2, pose === 'windup' ? '#e07a3d' : '#7fbfa3');
  void pose;
}

export function drawClerk(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const frame = pose === 'walk' ? Math.floor(time * 7) % 2 : 0;
  shadow(ctx, x, y, 12);
  blot(ctx, x - 4, y - 7 - frame, 3, 7, '#1a1814');
  blot(ctx, x + 1, y - 7 - (1 - frame), 3, 7, '#1a1814');
  box(ctx, x - 5, y - 19, 10, 12, '#2a2e36', ink);
  blot(ctx, x - 1, y - 18, 2, 8, '#e7e0d2');
  box(ctx, x - 4, y - 26, 8, 7, '#3a3e46', ink);
  const glow = pose === 'windup' ? '#f4f1ea' : '#7fbfb2';
  blot(ctx, x - 3, y - 24, 2, 2, glow);
  blot(ctx, x + 1, y - 24, 2, 2, glow);
  blot(ctx, x + 5, y - 14, 3, 4, '#c4a574');
  void time;
}

export function drawKettle(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const puff = pose === 'windup' ? 2 : 0;
  shadow(ctx, x, y, 26);
  box(ctx, x - 12 - puff, y - 20, 24 + puff * 2, 16, '#6a6560', ink);
  blot(ctx, x - 10, y - 18, 20, 3, '#8a8478');
  box(ctx, x + 11, y - 16, 7, 4, '#5c564e', ink);
  box(ctx, x - 5, y - 26, 10, 6, '#5c564e', ink);
  blot(ctx, x - 7, y - 12, 14, 2, '#e07a3d');
  for (let i = 0; i < 3; i++) {
    const t = (time * 1.4 + i / 3) % 1;
    ctx.fillStyle = `rgba(235,228,212,${(0.5 * (1 - t)).toFixed(2)})`;
    ctx.fillRect(Math.round(x + 16 + t * 6), Math.round(y - 18 - t * 14), 3, 3);
  }
  blot(ctx, x - 9, y - 4, 4, 3, '#3a3228');
  blot(ctx, x + 5, y - 4, 4, 3, '#3a3228');
}

export function drawMaster(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const frame = pose === 'walk' ? Math.floor(time * 5) % 2 : 0;
  const puff = pose === 'windup' ? 2 : 0;
  shadow(ctx, x, y, 22);
  blot(ctx, x - 7, y - 10 - frame, 5, 10, '#1a1814');
  blot(ctx, x + 2, y - 10 - (1 - frame), 5, 10, '#1a1814');
  box(ctx, x - 10 - puff, y - 30, 20 + puff * 2, 20, '#23262c', ink);
  blot(ctx, x - 1, y - 29, 2, 16, '#a34532');
  blot(ctx, x - 8, y - 27, 4, 2, '#c4a574');
  box(ctx, x - 7, y - 40, 14, 10, '#2f333a', ink);
  box(ctx, x - 9, y - 42, 18, 3, '#1a1814', ink);
  const on = Math.floor(time * 3) % 2 === 0;
  blot(ctx, x - 5, y - 37, 3, 3, on ? '#f4f1ea' : '#7fbfb2');
  blot(ctx, x + 2, y - 37, 3, 3, on ? '#7fbfb2' : '#f4f1ea');
}

export function drawWanderer(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const frame = pose === 'walk' ? Math.floor(time * 4) % 2 : 0;
  shadow(ctx, x, y, 18);
  blot(ctx, x - 5, y - 14 - frame, 3, 14, '#3a3228');
  blot(ctx, x + 2, y - 14 - (1 - frame), 3, 14, '#3a3228');
  box(ctx, x - 9, y - 36, 18, 22, '#4a3a5a', ink);
  blot(ctx, x - 9, y - 18, 18, 3, '#7a4b6e');
  box(ctx, x - 6, y - 46, 12, 10, '#b48cdc', ink);
  blot(ctx, x - 4, y - 42, 8, 3, '#1a1612');
  blot(ctx, x - 3, y - 41, 2, 1, '#f4f1ea');
  blot(ctx, x + 1, y - 41, 2, 1, '#f4f1ea');
  line(ctx, x + 10, y - 30, x + 12, y + 1, '#8a6a42', 2);
  const g = 0.3 + Math.sin(time * 3) * 0.2;
  ctx.fillStyle = `rgba(180,140,220,${g.toFixed(2)})`;
  ctx.fillRect(Math.round(x + 9), Math.round(y - 34), 5, 5);
}

export function drawForeman(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  drawMaster(ctx, x, y, pose, time, flash);
  // A hard hat and a tool belt. He keeps the works, not the timetable.
  box(ctx, x - 9, y - 45, 18, 5, '#e0b15a');
  blot(ctx, x - 11, y - 41, 22, 2, '#c4a15a');
  blot(ctx, x - 10, y - 16, 20, 3, '#6d5432');
}

/** Something half drawn. The pencil lines wobble; the fill never got done. */
export function drawBlot(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : '#3a3630';
  const wob = Math.floor(time * 6) % 2;
  shadow(ctx, x, y, 14);
  // Body: an outline with a patch of fill that is somewhere else each moment.
  blot(ctx, x - 7, y - 16, 14, 1, ink);
  blot(ctx, x - 7, y - 3, 14, 1, ink);
  blot(ctx, x - 7 - wob, y - 16, 1, 14, ink);
  blot(ctx, x + 6 + wob, y - 16, 1, 14, ink);
  blot(ctx, x - 5 + wob * 3, y - 14 + wob * 2, 6, 6, '#1a1612');
  blot(ctx, x - 3, y - 12, 2, 2, pose === 'windup' ? '#d4543a' : '#efeae0');
  blot(ctx, x + 2, y - 12, 2, 2, pose === 'windup' ? '#d4543a' : '#efeae0');
  // Legs, drawn as ticks.
  const step = pose === 'walk' ? Math.floor(time * 8) % 2 : 0;
  blot(ctx, x - 5, y - 3, 1, 3 + step, ink);
  blot(ctx, x + 4, y - 3, 1, 4 - step, ink);
}

/** Tall, in a long coat, with a measuring chain and a sighting staff. */
export function drawSurveyor(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const frame = pose === 'walk' ? Math.floor(time * 4) % 2 : 0;
  shadow(ctx, x, y, 22);
  blot(ctx, x - 6, y - 12 - frame, 4, 12, '#2a2622');
  blot(ctx, x + 2, y - 12 - (1 - frame), 4, 12, '#2a2622');
  box(ctx, x - 10, y - 36, 20, 26, '#8a8474', ink);
  blot(ctx, x - 10, y - 20, 20, 2, '#6a6458');
  // The fill stops halfway down the coat, the way the ground does.
  blot(ctx, x - 9, y - 18, 18, 7, '#e9e2cf');
  box(ctx, x - 6, y - 46, 12, 10, '#e9e2cf', ink);
  blot(ctx, x - 8, y - 48, 16, 3, '#3a3630');
  // One eye, through a sight.
  blot(ctx, x - 1, y - 43, 4, 4, '#1a1612');
  blot(ctx, x, y - 42, 2, 2, pose === 'windup' ? '#d4543a' : '#e0b15a');
  line(ctx, x + 12, y - 50, x + 12, y + 1, '#c4a574', 2);
  for (let i = 0; i < 5; i++) blot(ctx, x + 11, y - 48 + i * 10, 4, 2, i % 2 ? '#d4543a' : '#efeae0');
  const swing = Math.sin(time * 3) * 3;
  for (let i = 0; i < 6; i++) blot(ctx, x - 12 - i + swing * (i / 6), y - 26 + i * 4, 2, 2, '#c4a574');
}

/** A coiled spring the size of a room, still letting go. */
export function drawMainspring(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#f4f1ea' : INK;
  const turn = time * (pose === 'windup' ? 6 : 2);
  shadow(ctx, x, y, 28);
  box(ctx, x - 14, y - 34, 28, 30, '#3a3228', ink);
  for (let r = 12; r >= 3; r -= 3) {
    const a = turn + r;
    const cx = x + Math.cos(a) * 1.5;
    const cy = y - 19 + Math.sin(a) * 1.5;
    ctx.fillStyle = r % 2 ? '#c47a4a' : '#8a4a32';
    ctx.fillRect(Math.round(cx - r), Math.round(cy - r), r * 2, 2);
    ctx.fillRect(Math.round(cx - r), Math.round(cy + r - 2), r * 2, 2);
    ctx.fillRect(Math.round(cx - r), Math.round(cy - r), 2, r * 2);
    ctx.fillRect(Math.round(cx + r - 2), Math.round(cy - r), 2, r * 2);
  }
  blot(ctx, x - 2, y - 21, 4, 4, '#e0b15a');
  const tick = Math.floor(time * 2) % 2;
  blot(ctx, x + 13, y - 30 + tick * 3, 5, 2, '#e0b15a');
  blot(ctx, x - 18, y - 12 - tick * 3, 5, 2, '#e0b15a');
}

/** A nought, standing up. Round, empty, patient. */
export function drawLastNumber(ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean): void {
  const ink = flash ? '#1a1612' : '#f4f1ea';
  const bob = Math.round(Math.sin(time * 2) * 2);
  shadow(ctx, x, y, 26);
  const top = y - 48 + bob;
  // Thick white ring, and nothing in the middle but the floor behind it.
  ctx.fillStyle = INK;
  ctx.fillRect(Math.round(x - 13), top - 1, 26, 46);
  ctx.fillStyle = ink;
  ctx.fillRect(Math.round(x - 12), top, 24, 44);
  ctx.fillStyle = '#0c0a08';
  ctx.fillRect(Math.round(x - 6), top + 7, 12, 30);
  // Numbers run round the ring, counting down to it.
  const n = Math.floor(time * 4);
  const glow = pose === 'windup' ? '#d4543a' : '#e0b15a';
  for (let i = 0; i < 6; i++) {
    const k = (n + i) % 6;
    blot(ctx, x - 11 + (k % 3) * 9, k < 3 ? top + 2 : top + 39, 3, 3, i === 0 ? glow : '#8a8478');
  }
  const g = 0.25 + Math.sin(time * 3) * 0.15;
  ctx.fillStyle = `rgba(239,234,224,${g.toFixed(2)})`;
  ctx.fillRect(Math.round(x - 5), top + 8, 10, 28);
}
